import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentAccountNameV2Data,
  type EnrollmentAccountNameV2SubmitInput,
  type EnrollmentAccountNameV2SubmitOutput,
} from '../../../../../models'
import { createMutationHook, createQueryHook } from '../../../../../hooks'

/**
 * Get Enrollment AccountNameV2 data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentAccountNameV2Data>
 * @example const response = await getEnrollmentAccountNameV2Data()
 */
export const getEnrollmentAccountNameV2Data = async (
  context?: DefaultContext,
): Promise<EnrollmentAccountNameV2Data> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentAccountNameV2Data',
      query: `mutation getEnrollmentAccountNameV2Data($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
              recommendation {
                investment {
                  account {
                    accountNickname
                  }
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentAccountNameV2Data }
  return response.submitEnrollment
}

export const useGetEnrollmentAccountNameV2Data = createQueryHook<undefined, EnrollmentAccountNameV2Data>(
  getEnrollmentAccountNameV2Data,
)

/**
 * Submit Enrollment AccountNameV2 data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentAccountNameV2SubmitOutput>
 * @example const response = await submitEnrollmentAccountNameV2Data();
 */
export const submitEnrollmentAccountNameV2Data = async (
  enrollmentAccountNameV2SubmitInput: EnrollmentAccountNameV2SubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentAccountNameV2SubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentAccountNameV2Data',
      query: `mutation submitEnrollmentAccountNameV2Data($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentAccountNameV2SubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentAccountNameV2SubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentAccountNameV2Data = createMutationHook<
  EnrollmentAccountNameV2SubmitInput,
  EnrollmentAccountNameV2SubmitOutput
>(submitEnrollmentAccountNameV2Data)
